import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * ScrollToTop - The Nuclear Option
 * 
 * This component aggressively forces the window to scroll to (0,0) on every route change.
 * It uses a loop over several animation frames to defeat browser scroll restoration
 * and any other race conditions.
 */
function ScrollToTop() {
    const { pathname } = useLocation();

    useLayoutEffect(() => {
        // 1. Disable browser's default scroll restoration immediately
        if ('scrollRestoration' in window.history) {
            window.history.scrollRestoration = 'manual';
        }

        // 2. The "Aggressive" Scroll Function
        const forceScroll = () => {
            // Priority: If Lenis is active, tell it to scroll immediately
            if (window.lenis) {
                window.lenis.scrollTo(0, { immediate: true });
            }

            // Also force native scroll as fallback/redundancy
            window.scrollTo(0, 0);
            document.documentElement.scrollTop = 0;
            document.body.scrollTop = 0;
        };

        // 3. Fire immediately
        forceScroll();

        // 4. Fire repeatedly for the next few frames to ensure it sticks
        // This handles cases where layout might shift or browser tries to restore position
        let frameId;
        let count = 0;
        const maxFrames = 10; // Run for ~160ms (at 60fps)

        const loop = () => {
            forceScroll();
            count++;
            if (count < maxFrames) {
                frameId = requestAnimationFrame(loop);
            }
        };

        frameId = requestAnimationFrame(loop);

        return () => {
            cancelAnimationFrame(frameId);
        };
    }, [pathname]);

    return null;
}

export default ScrollToTop;
